import { User } from '../types';

// Url of an avatar, '' without avatar: MUI shows its default icon instead of asking ".../undefined".
const getAvatarUrl = (avatar?: string) => (avatar ? `${process.env.REACT_APP_AWS_CLOUDFRONT_URL}${avatar}` : '');

// Same minimum versions as VIDEO_MODERATED_MIN_VERSION on soskills-server:
// below them the app can't display the videoModerated notification.
const VIDEO_MODERATED_MIN_VERSION = {
  ios: '2.3.44',
  android: '0.99.89',
} as { [os: string]: string };

const isVersionGte = (version: string, minVersion: string) => {
  const current = version.split('.').map((n) => parseInt(n, 10));
  const min = minVersion.split('.').map((n) => parseInt(n, 10));
  for (let i = 0; i < min.length; i += 1) {
    const c = current[i] || 0;
    if (c > min[i]) {
      return true;
    }
    if (c < min[i]) {
      return false;
    }
  }
  return true;
};

const getVersionOn = (user: Partial<User>, os: string) => (os === 'ios' ? user.versionApple : user.versionAndroid) || '';

const isRecentOn = (user: Partial<User>, os: string) => {
  const version = getVersionOn(user, os);
  return !!version && isVersionGte(version, VIDEO_MODERATED_MIN_VERSION[os]);
};

// The app shown to the admin: one recent enough to get the removal, the current one (connectedWith) first.
// connectedWith alone is not enough: it is only set once push is allowed.
const getAppOs = (user: Partial<User>) => {
  const used = [user.connectedWith || '', 'ios', 'android']
    .filter((os, i, all) => !!os && all.indexOf(os) === i && !!getVersionOn(user, os));
  return used.find((os) => isRecentOn(user, os)) || used[0] || '';
};

// "iOS 2.3.51", "Android 0.99.40" or '' when the user never used the app.
const getAppLabel = (user: Partial<User>) => {
  const os = getAppOs(user);
  if (!os) {
    return '';
  }
  return `${os === 'ios' ? 'iOS' : 'Android'} ${getVersionOn(user, os)}`;
};

const getMinVersionLabel = (user: Partial<User>) => {
  const os = getAppOs(user);
  if (!os) {
    return '';
  }
  return `${os === 'ios' ? 'iOS' : 'Android'} ${VIDEO_MODERATED_MIN_VERSION[os]}`;
};

// Same rule as the server: one recent app is enough, push allowed or not.
const canReceiveVideoModerated = (user: Partial<User>) => {
  const os = getAppOs(user);
  return !!os && isRecentOn(user, os);
};

// Value of fcmPermissionStatus shown in the admin: authorized, denied, token_expired or none.
const getPushStatus = (user: Partial<User>) => {
  if (user.fcmPermissionStatus === 'authorized' || user.fcmPermissionStatus === 'provisional') {
    return 'authorized';
  }
  if (user.fcmPermissionStatus === 'denied' || user.fcmPermissionStatus === 'token_expired') {
    return user.fcmPermissionStatus;
  }
  return 'none';
};

// Notice shown before a removal: will the creator get the reason in the app?
// notified: false when the admin has to tell the creator himself.
const getRemovalNotice = (user: Partial<User>, t: any) => {
  const username = user.username;
  const app = getAppLabel(user);
  if (!app) {
    return { severity: 'warning' as const, title: '', text: t('admin.videos.noApp', { username }), notified: false };
  }
  if (canReceiveVideoModerated(user)) {
    return {
      severity: 'success' as const,
      title: '',
      text: t('admin.videos.willBeNotified', { app, username }),
      notified: true,
    };
  }
  return {
    severity: 'warning' as const,
    title: t('admin.videos.willBeSuspended'),
    text: t('admin.videos.willNotBeNotified', { app, username, min: getMinVersionLabel(user) }),
    notified: false,
  };
};

// Same rule as the server: French creators read French, all the others English.
const getModerationLanguage = (language?: string) => (language === 'fr' ? 'fr' : 'en');

// "Envoyé en anglais · langue du joueur : en · America/New_York"
const getSentInLabel = (user: Partial<User>, t: any) => [
  t('admin.videos.sentIn', { language: t(`admin.videos.languageNames.${getModerationLanguage(user.language)}`) }),
  t('admin.videos.playerLanguage', { code: user.language || t('admin.videos.unknownLanguage') }),
  user.timezone,
].filter(Boolean).join(' · ');

// "Anglais", "English"... in the language of the admin, the code when unknown.
const getLanguageLabel = (code: string | undefined, language: string) => {
  if (!code) {
    return '';
  }
  try {
    const label = new Intl.DisplayNames([language], { type: 'language' }).of(code) || code;
    return label.charAt(0).toUpperCase() + label.slice(1);
  } catch (e) {
    return code.toUpperCase();
  }
};

export {
  getAvatarUrl,
  canReceiveVideoModerated,
  getRemovalNotice,
  getLanguageLabel,
  getModerationLanguage,
  getSentInLabel,
  getAppLabel,
  getMinVersionLabel,
  getPushStatus,
};
